/**
 * @fileoverview Shared state and refs for the NotificationForm page.
 * @module pages/notificationForm/useNotificationFormContext
 */
import { use, useRef, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { AppContext } from "../../context/AppContext";
import useRequireAccess from "../../hooks/useRequireAccess.js";
import { canAccessCustomNotifications } from "../../utils/accessControl.js";
import { getSectors } from "../../logic/sectors/index.js";
import { initializeNotificationFormPage } from "../../logic/customNotification/index.js";
import {
  getNotificationTypeOptions,
  getCompanySizeOptions,
  getCompanyGeneralActivityOptions,
} from "../../data/selectOptions.js";

const INITIAL_FORM_DATA = {
  tipo: null,
  audienciaParaSocios: false,
  audienciaParaNoSocios: false,
  tamanoDeLaEmpresa: null,
  sector: null,
  subSector: null,
  actividadGeneralDeLaEmpresa: null,
  productoPrincipal: null,
  fechaAltaSac: null,
  asunto: null,
  descripcion: null,
  imagenUrl: null,
};

/** Holds NotificationForm page state, router helpers, and refs. */
const useNotificationFormContext = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { allowed } = useRequireAccess(canAccessCustomNotifications);
  const { changePermissions } = use(AppContext);

  const toastId = useRef(null);
  const pendingUploadedImageRef = useRef(null);
  const imageRef = useRef(null);
  const selectSubsectorRef = useRef(null);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [newObjectId, setNewObjectId] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [sectors, setSectors] = useState([]);
  const [subsectors, setSubsectors] = useState([]);
  const [showError, setShowError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const [error, setError] = useState(null);

  const notificationTypeOptions = getNotificationTypeOptions(t);
  const companySizeOptions = getCompanySizeOptions(t);
  const companyGeneralActivityOptions = getCompanyGeneralActivityOptions(t);

  useEffect(() => {
    const initializeComponent = async () => {
      const token = localStorage.getItem("token");
      if (!token || token.trim() === "undefined") {
        return;
      }
      const userId = localStorage.getItem("userId");
      await initializeNotificationFormPage(
        userId,
        changePermissions,
        getSectors,
        setSectors,
        setLoading,
        navigate,
      );
    };
    initializeComponent();
  }, [navigate, changePermissions]);

  return {
    t,
    i18n,
    navigate,
    allowed,
    toastId,
    pendingUploadedImageRef,
    imageRef,
    selectSubsectorRef,
    formData,
    setFormData,
    newObjectId,
    setNewObjectId,
    imageFile,
    setImageFile,
    imagePreview,
    setImagePreview,
    sectors,
    subsectors,
    setSubsectors,
    showError,
    setShowError,
    loading,
    isCalendarOpen,
    setIsCalendarOpen,
    error,
    setError,
    notificationTypeOptions,
    companySizeOptions,
    companyGeneralActivityOptions,
  };
};

export default useNotificationFormContext;
